import Language from '../../i18n/Language';

const t = Language;

/**
 * FAQ / How-To 结构化数据「设置」弹窗的派生统计计算（纯函数，无 React 状态）
 *
 * 所有函数都显式接收 `statistics` / `config`（来自 reqGetJLPageData 的 res.data），
 * 便于在任意页面（SnippetsJSONLD 设置页、Content Audit 详情页等）复用，避免逻辑分叉。
 */

// page_type 选项：在调用时构建，保证语言切换后文案生效
export const getPageOptions = (moduleType) => (
    moduleType === 'How-To Schema'
        ? [
            { label: t('Blog Post'), value: 'article' },
            { label: t('Other page'), value: 'page' },
        ]
        : [
            { label: t('Product'), value: 'product' },
            { label: t('Blog Post'), value: 'article' },
            { label: t('Collection'), value: 'collection' },
            { label: t('Other page'), value: 'page' },
        ]
);

export const getDefaultSelectedPages = (moduleType) => (
    moduleType === 'How-To Schema' ? ['article'] : ['product', 'article']
);

// moduleType ('FAQ Schema' | 'How-To Schema') -> statistics/config 里的 key
export const getModuleName = (moduleType) => (moduleType === 'How-To Schema' ? 'howto' : 'faq');

// 格式化重置日期 (unix 秒 -> "May 31")
export const formatResetDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${t(months[date.getMonth()])} ${date.getDate()}`;
};

export const getAiCredits = (statistics) => (
    statistics?.ai_credits || { used: 0, total: 0, points_per_page: 0.1, period_reset_date: null }
);

// 根据选中的 page_types 聚合 generated / analyzed / pending
export const sumModuleStats = (statistics, moduleName, types) => {
    const byType = statistics?.[moduleName]?.by_type || {};
    let generated = 0, analyzed = 0, pending = 0;
    (types || []).forEach((type) => {
        generated += byType[type]?.generated || 0;
        analyzed += byType[type]?.analyzed || 0;
        pending += byType[type]?.pending || 0;
    });
    return { generated, analyzed, pending };
};

// 根据开关 / 积分 / 超额 推断分析模块状态
// 0 - 未启用开关 且 有 pages 未分析 -> "X pages to analyze" (X = pending)
// 1 - 所选页面类型下所有页面已分析 (pending === 0, 无论开关) -> "All X pages analyzed" (X = analyzed)
// 2 - 开关开启 + 额度耗尽 + 有 pending -> "X pages pending" (X = pending)
// 3 - 开关开启 + 有 pages 在 analyzing 队列 -> "Analyzing pages..."
export const computeAnalyzeState = ({ statistics, config, moduleName, moduleStats }) => {
    const aiCredits = getAiCredits(statistics);
    const pointsPerPage = aiCredits.points_per_page || 0.1;
    const remainingCredits = (aiCredits.total || 0) - (aiCredits.used || 0);
    const overageEnabled = Boolean(statistics?.overage?.enabled);
    const enabled = Boolean(config?.[moduleName]?.enabled);
    if (moduleStats.pending === 0) {
        return { state: 1, num: moduleStats.analyzed };
    }
    if (!enabled) {
        return { state: 0, num: moduleStats.pending };
    }
    const pendingCredits = Math.ceil(moduleStats.pending * pointsPerPage);
    const creditsExhausted = remainingCredits < pendingCredits && !overageEnabled;
    if (creditsExhausted) {
        return { state: 2, num: moduleStats.pending };
    }
    return { state: 3, num: 0 };
};

// 构建 SchemaSettingsModal 需要的 config（即原 settingsCfg）
export const buildSettingsCfg = ({ statistics, config, moduleType, selectedPages }) => {
    if (!moduleType) return null;
    const moduleName = getModuleName(moduleType);
    const aiCredits = getAiCredits(statistics);
    const pointsPerPage = aiCredits.points_per_page || 0.1;
    const remainingCredits = (aiCredits.total || 0) - (aiCredits.used || 0);
    const moduleStats = sumModuleStats(statistics, moduleName, selectedPages);
    const moduleEnabled = Boolean(config?.[moduleName]?.enabled);
    const pendingPages = moduleStats.pending;
    const pendingCredits = Math.ceil(pendingPages * pointsPerPage);
    const remainingForModule = Math.max(0, remainingCredits);
    const creditsInsufficient = remainingCredits < pendingCredits;
    // 缺口积分 (向上取整 to pages → credits 已经在 pendingCredits 完成)
    const extraCreditsNeeded = Math.max(0, pendingCredits - remainingCredits);
    // 超额费用 = ceil(缺口积分 / 100) × $9.90 (规避 JS 浮点精度问题，再做一次保留两位小数的圆整)
    const billingPrice = Math.round(Math.ceil(extraCreditsNeeded / 100) * 9.9 * 100) / 100;
    // 用完积分后会暂停的页面数 = pending - 剩余积分 × 10
    const pagesWillPause = Math.max(0, pendingPages - remainingForModule * 10);
    // 与 analyzeModule 对齐的状态值
    const analyzeState = computeAnalyzeState({ statistics, config, moduleName, moduleStats }).state;
    return {
        used: aiCredits.used || 0,
        total: aiCredits.total || 0,
        resetDate: formatResetDate(aiCredits.period_reset_date),
        pendingPages,
        pendingCredits,
        analyzed: moduleStats.analyzed,
        schemaCount: moduleStats.generated,
        analyzeState,
        moduleEnabled,
        creditsInsufficient,
        extraCreditsNeeded,
        billingPrice,
        pagesWillPause,
    };
};
